import React from "react";
import { DropzoneRootProps, useDropzone } from "react-dropzone";
import { cn } from "../../../../lib/utils";

import { useIsWindowDragActive } from "../../../../libs/drag-and-drop";

import DropBoxIcon from "./icons/dropbox-icon.svg";
import GoogleDriveIcon from "./icons/google-drive-icon.svg";
import CanvaIcon from "./icons/canva-logo.svg";
import FrontifyIcon from "./icons/frontify-icon.svg";
import { FileToUpload } from "../../../../api/media-library";
import { useSelector } from "react-redux";
import { hasFrontify } from "../../../../utils/feature-gates";
import { getUser } from "../../../../selectors/commonSelectors";
import { useTrackEvent } from "../../../../libs/analytics";
import { Icon } from "@dialogueconsulting/sked-ui";
import { goToManagePlan } from "../../../../utils/manage-plan";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Progress } from "../../../../components/ui/progress";
import { useFileSelectionHandlers } from "../../hooks/useFileSelectionHandlers";
import { UploadBatchSummary, UploadingFile } from "../../uploading-files.model";
import { UploadBatchList } from "./upload-batch-list";
import { GoogleDriveButton } from "./google-drive-button";

interface Props {
  className?: string;
  title: string;
  limit: number;
  uploadingStatus: string | null;
  uploadingProgressValue?: number | null;
  uploads?: UploadingFile[];
  uploadSummary?: UploadBatchSummary;
  isDisabled: boolean;

  onSelect(files: FileToUpload[]): void;
  onCancelUpload?(id: string): void;
  onRetryUpload?(id: string): void;
  onDoneUploads?(): void;

  renderOverlay(props: DropzoneRootProps): React.ReactNode;
}

export const FileSelector: React.FC<Props> = ({
  className,
  title,
  onSelect,
  renderOverlay,
  limit,
  uploadingStatus,
  uploadingProgressValue = null,
  uploads,
  uploadSummary,
  onCancelUpload,
  onRetryUpload,
  onDoneUploads,
  isDisabled,
}) => {
  const trackEvent = useTrackEvent();

  // Extract folder name from title, handling undefined gracefully
  const folderName = title ? title.replace("Upload into ", "").trim() : "";
  const shouldShowFolder = folderName && folderName !== "undefined";

  const {
    onDrop,
    handleDropBoxClick,
    handleGoogleDriveClick,
    handleGoogleDriveSwitchAccount,
    googleDriveSession,
    handleCanvaTypeClick,
    handleCanvaClick,
    handleFrontifyClick,
    isCanva,
    dropzoneConfig,
  } = useFileSelectionHandlers({
    onSelect,
    limit,
    isDisabled,
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...dropzoneConfig,
    onDrop,
  });
  const isWindowDragActive = useIsWindowDragActive();

  const user = useSelector(getUser);
  const frontifyEnabled = hasFrontify(user);

  const handleFrontifyUpsell = () => {
    trackEvent({
      eventName: "Upgrade-frontify-viewed",
      useServices: ["AMP", "HS"],
    });
    goToManagePlan();
  };

  if (uploads && uploads.length > 0 && uploadSummary) {
    return (
      <div className="mb-5 flex min-h-[175px] max-h-[420px] flex-col rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
        <UploadBatchList
          uploads={uploads}
          summary={uploadSummary}
          destination={shouldShowFolder ? folderName : undefined}
          onCancel={(id) => onCancelUpload?.(id)}
          onRetry={(id) => onRetryUpload?.(id)}
          onDone={() => onDoneUploads?.()}
        />
      </div>
    );
  }

  if (uploadingStatus !== null) {
    const progressValue = Math.max(
      0,
      Math.min(100, uploadingProgressValue ?? 0)
    );

    return (
      <div className="mb-5 flex min-h-[175px] max-h-[348px] flex-col items-center justify-center rounded-lg bg-neutral-50 px-6 py-8">
        <div className="mb-6 text-center text-[18px] font-semibold leading-7 text-neutral-800">
          {title}
        </div>
        <div className="w-full max-w-[720px] space-y-2">
          <div className="flex items-center justify-between gap-4 text-sm font-medium leading-5 text-neutral-700">
            <span>{uploadingStatus}</span>
            <span>{progressValue}%</span>
          </div>
          <Progress
            value={progressValue}
            className="h-3 rounded-full bg-neutral-200"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "flex flex-col items-center rounded-lg min-h-[175px] mb-5",
          className
        )}
        {...getRootProps()}
      >
        {/* Upload Dropzone */}
        <div
          className={cn(
            // Base styles - dashed border, white background
            "w-full border border-dashed border-[rgba(26,24,35,0.12)] rounded-lg",
            "px-6 py-4 bg-white",
            "cursor-pointer transition-colors",
            "flex flex-col items-center gap-2",

            // Hover state
            "hover:bg-[#f9f9f9]",

            // Dragging state
            (isDragActive || isWindowDragActive) && "bg-blue-50 border-blue-300"
          )}
        >
          {/* Cloud upload icon */}
          <div className="size-[18px] text-neutral-500 mt-2">
            <i className="fa-regular fa-cloud-arrow-up text-lg" />
          </div>

          {/* Text with two-tone colors */}
          <div className="text-sm leading-5 text-center font-inter">
            <span className="underline text-[#1a1823] font-medium">Click</span>
            <span className="text-[#676571]">
              {" "}
              or drag and drop file here to upload
            </span>
            {shouldShowFolder && (
              <>
                <span className="text-[#676571]"> to folder </span>
                <span className="text-[#1a1823] font-medium">
                  "{folderName}"
                </span>
              </>
            )}
          </div>

          {/* Helper text */}
          <p className="text-xs leading-4 text-[#676571]">
            Files Supported: JPG, PNG, GIF, PDF or MP4 (max size 100mb)
          </p>

          {/* Hidden file input */}
          <label className="block w-full cursor-pointer">
            <input
              {...getInputProps()}
              disabled={isDisabled}
              className="hidden"
            />
          </label>
        </div>

        {/* Integration Buttons - stopPropagation prevents dropzone from opening file picker */}
        <div
          className="flex items-center gap-2 mt-3 w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drive Button */}
          <GoogleDriveButton
            session={googleDriveSession}
            onPick={handleGoogleDriveClick}
            onSwitchAccount={handleGoogleDriveSwitchAccount}
          >
            <button
              type="button"
              data-testid="platformGoogleDriveButton"
              disabled={isDisabled}
              className={cn(
                "flex-1 h-9 px-3 py-2",
                "border border-[rgba(26,24,35,0.12)] rounded-lg bg-white",
                "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08)]",
                "flex items-center justify-center gap-2",
                "font-inter text-sm font-medium text-neutral-700",
                "transition-colors cursor-pointer",
                "hover:bg-[#f9f9f9]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <GoogleDriveIcon className="h-4 w-4" />
              <span>Drive</span>
            </button>
          </GoogleDriveButton>

          {/* Canva Button */}
          <button
            type="button"
            data-testid="platformCanvaButton"
            disabled={isDisabled}
            onClick={handleCanvaClick}
            className={cn(
              "flex-1 h-9 px-3 py-2",
              "border rounded-lg bg-white",
              "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08)]",
              "flex items-center justify-center gap-2",
              "font-inter text-sm font-medium text-neutral-700",
              "transition-colors cursor-pointer",
              "hover:bg-[#f9f9f9]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              isCanva
                ? "border-blue-500 bg-blue-50"
                : "border-[rgba(26,24,35,0.12)]"
            )}
          >
            <CanvaIcon className="h-4 w-4" />
            <span>Canva</span>
          </button>

          {/* Dropbox Button */}
          <button
            type="button"
            data-testid="platformDropboxButton"
            disabled={isDisabled}
            onClick={handleDropBoxClick}
            className={cn(
              "flex-1 h-9 px-3 py-2",
              "border border-[rgba(26,24,35,0.12)] rounded-lg bg-white",
              "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08)]",
              "flex items-center justify-center gap-2",
              "font-inter text-sm font-medium text-neutral-700",
              "transition-colors cursor-pointer",
              "hover:bg-[#f9f9f9]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            <DropBoxIcon className="h-4 w-4" />
            <span>Dropbox</span>
          </button>

          {/* Ellipsis Menu for Frontify */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={isDisabled}
                className={cn(
                  "h-9 px-3 py-2 relative",
                  "border border-[rgba(26,24,35,0.12)] rounded-lg bg-white",
                  "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08)]",
                  "flex items-center justify-center",
                  "font-inter text-sm font-medium text-neutral-700",
                  "transition-colors cursor-pointer",
                  "hover:bg-[#f9f9f9]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {!frontifyEnabled && (
                  <Icon
                    name="upsell-rocket-blue-bg"
                    size="xs"
                    className="absolute -top-1 -right-1 z-10"
                  />
                )}
                <i className="fa-regular fa-ellipsis" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid="platformFrontifyButton"
                onClick={
                  frontifyEnabled ? handleFrontifyClick : handleFrontifyUpsell
                }
                className="gap-2"
              >
                <FrontifyIcon className="h-4 w-4" />
                <span>Choose from Frontify</span>
                {!frontifyEnabled && (
                  <Icon
                    name="upsell-rocket-blue-bg"
                    size="xs"
                    className="ml-auto"
                  />
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Canva Type Selection - stopPropagation prevents dropzone from opening file picker */}
        {isCanva && !isDisabled && (
          <div
            className="flex flex-col justify-evenly items-center w-full relative mt-3 mb-3.5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center items-center w-full relative">
              <Button
                variant="blue-primary"
                size="xs"
                onClick={() => handleCanvaTypeClick("SocialMedia")}
                className="m-1 !font-normal !h-[30px] !leading-[30px] !text-[10px]"
              >
                Generic Social Media
              </Button>
              <Button
                variant="blue-primary"
                size="xs"
                onClick={() => handleCanvaTypeClick("InstagramPost")}
                className="m-1 !font-normal !h-[30px] !leading-[30px] !text-[10px]"
              >
                Instagram Post
              </Button>
              <Button
                variant="blue-primary"
                size="xs"
                onClick={() => handleCanvaTypeClick("InstagramStory")}
                className="m-1 !font-normal !h-[30px] !leading-[30px] !text-[10px]"
              >
                Instagram Story
              </Button>
            </div>
            <div className="flex justify-center items-center w-full relative">
              <Button
                variant="blue-primary"
                size="xs"
                onClick={() => handleCanvaTypeClick("FacebookPost")}
                className="m-1 !font-normal !h-[30px] !leading-[30px] !text-[10px]"
              >
                Facebook Post
              </Button>
              <Button
                variant="blue-primary"
                size="xs"
                onClick={() => handleCanvaTypeClick("TwitterPost")}
                className="m-1 !font-normal !h-[30px] !leading-[30px] !text-[10px]"
              >
                Twitter Post
              </Button>
            </div>
          </div>
        )}
      </div>
      {isWindowDragActive && renderOverlay(getRootProps())}
    </>
  );
};
